import React, {ChangeEvent, useEffect, useState} from 'react';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import { useForm } from "react-hook-form";
import {Box, Button, makeStyles} from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import FormHelperText from '@material-ui/core/FormHelperText';
import TerminosCondiciones from "../Dialogs/TerminosCondiciones";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import {Visibility, VisibilityOff} from "@material-ui/icons";
import InputLabel from "@material-ui/core/InputLabel";
import FilledInput from "@material-ui/core/FilledInput";
import FormControl from '@material-ui/core/FormControl';
import * as helperValidations from "../Validations/HelperValidations";
import OtpModal from "../Otp/OtpModal";
import ProductRequest from "../../models/request/ProductRequest";
import AccountRequest from "../../models/request/AccountRequest";
import { useHistory } from 'react-router-dom';
import {BFF_ONBOARDING_URL} from "../../index";
import {decryptSymmetric} from "../../utils/DecryptionUtils";
import {Establishment} from "../../models/Establishment";

const colorBlue = "#023F86";

const useStyles = makeStyles({
    buttonContinuarStyle: {
        backgroundColor: colorBlue,
        color: '#FFFFFF',
        width: 230,
        height: 70
    },
    buttonAction: {
        display: 'flex',
        justifyContent: 'flex-end',
        paddingTop: '2rem',
    },
});

interface AccountType {
    cuit: string,
    handleNext(email: string): void,
    productRequest: ProductRequest,
    establishments: Establishment[]
}

const AccountForm : React.FC<AccountType> = ({cuit, handleNext, productRequest, establishments}) => {

    const [name, setName] = useState('');
    const [surname, setSurname] = useState('');
    const [email, setEmail] = useState('');
    const [emailConfirmation, setEmailConfirmation] = useState('');
    const [password, setPassword] = useState('');
    const [checked, setChecked] = React.useState(false);
    const [nameIsValid, setNameIsValid] = useState(false);
    const [surnameIsValid, setSurnameIsValid] = useState(false);
    const [emailIsValid, setEmailIsValid] = useState(false);
    const [passwordIsValid, setPasswordIsValid] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [errorMsgName, setErrorMsgName] = useState('');
    const [errorMsgSurname, setErrorMsgSurname] = useState('');
    const [errorMsgEmail, setErrorMsgEmail] = useState('');
    const [errorMsgEmailConfirmation, setErrorMsgEmailConfirmation] = useState('');
    const [errorMsgPassword, setErrorMsgPassword] = useState('');
    const [errorEmailExists, setErrorEmailExists] = React.useState(false);
    const [showPassword, setShowPassword] = React.useState(false);
    const [showModal, setShowModal] = React.useState(false);
    const [modalWasConfirmed, setModalWasConfirmed] = React.useState(false);

    const { handleSubmit } = useForm();
    const history = useHistory()

    const onSubmit = () => {
        setLoading(true);
        setShowModal(true)
    };

    useEffect(() => {
        if(modalWasConfirmed) {
            const accountCreationProcess = async () => {
                existsEmail(email)
                   .then(async exists => {
                          if (!exists) {
                              const accountRequest = await buildAccountRequest(productRequest)
                              history.push(`/accounts`, {
                                  accountRequest: accountRequest
                              });
                          }
                      }
                   ).catch()
            }
            accountCreationProcess()
        }
    }, [modalWasConfirmed]);

    const existsEmail = async (email: any) => {
        try {
            const response = await fetch(`${BFF_ONBOARDING_URL}/users/` + email, {
                method: 'get',
                headers: { 'Content-Type': 'application/json' },
            });
            switch (response.status) {
                case 200:
                    setErrorMsg('YA EXISTE UNA CUENTA REGISTRADA CON EL MAIL INGRESADO, POR FAVOR INGRESE OTRO MAIL.');
                    setErrorEmailExists(true);
                    setLoading(false);
                    return true;
                case 404:
                    setLoading(false);
                    return false;
                case 500:
                    setErrorMsg('Se produjo un error recuperando datos.');
                    setLoading(false);
                    return true;
                case 503:
                    setErrorMsg('Se produjo un error recuperando datos.');
                    setLoading(false);
                    return true;
                default:
                    setLoading(false);
                    return true;
            }
        } catch (error) {
            setErrorMsg(error.message);
            setLoading(false);
            return false;
        }
    }

    const buildAccountRequest = async (productRequest: ProductRequest): Promise<AccountRequest> => {
        const billingId = await getBillingIdByCuit(cuit)
        return {
                name: name,
                surname: surname,
                cuit: cuit,
                email: email,
                legal_name: establishments[0].business_name,
                address: establishments[0].address,
                city: establishments[0].city,
                zip_code: establishments[0].zip_code,
                billing_id: billingId,
                password: password,
                products: [productRequest]
        }
    }

    const getBillingIdByCuit = async (cuit: any) => {
        try {
            const response = await fetch(`${BFF_ONBOARDING_URL}/billings/` + cuit, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });
            switch (response.status) {
                case 200:
                    const encryptedData =  await response.json()
                    const decryptedData = await decryptSymmetric(encryptedData.encryptedData, encryptedData.iv)
                    const data = JSON.parse(decryptedData)
                    return data?.id
                case 500:
                    setErrorMsg('Se produjo un error recuperando datos.');
                    setLoading(false);
                    return null;
                case 503:
                    setErrorMsg('Se produjo un error recuperando datos.');
                    setLoading(false);
                    return null;
                default:
                    setLoading(false);
                    return null;
            }
        } catch (error) {
            setErrorMsg(error.message);
            setLoading(false);
            return null;
        }
    }

    const onChangeName = (event: any) => {
        const { value } = event.target;
        const name = value.trimStart();
        if( helperValidations.lengthBetween(name.length, 1, 200) ) {
            setNameIsValid(true);
            setErrorMsgName('');
        } else {
            setNameIsValid(false);
            if( helperValidations.exactLength(name.length, 0) ){
                setErrorMsgName('El campo nombre es obligatorio');
            } else {
                setErrorMsgName('El nombre no puede superar los 200 caracteres');
            }
        }
        setName(name);
    };

    const onChangeSurname = (event: any) => {
        const { value } = event.target;
        const surname = value.trimStart();
        if( helperValidations.lengthBetween(surname.length, 1, 200) ) {
            setSurnameIsValid(true);
            setErrorMsgSurname('');
        } else {
            setSurnameIsValid(false);
            if( helperValidations.exactLength(surname.length, 0) ){
                setErrorMsgSurname('El campo apellido es obligatorio');
            } else {
                setErrorMsgSurname('El apellido no puede superar los 200 caracteres');
            }
        }
        setSurname(surname);
    };

    const onChangeEmail = (event: any) => {
        const { value } = event.target;
        const email = value;
        if( helperValidations.lengthBetween(email.length, 1, 200) && helperValidations.emailFormat(String(email))  ) {
            if( email === emailConfirmation ){
                setEmailIsValid(true);
                setErrorMsgEmail('');
                setErrorMsgEmailConfirmation('');
            }else{
                setEmailIsValid(false);
                if( emailConfirmation !== "" ) {
                    setErrorMsgEmail('Los e-mails ingresados no coinciden');
                } else{
                    setErrorMsgEmail('');
                }
            }
        } else {
            setEmailIsValid(false);
            if( helperValidations.exactLength(email.length, 0) ){
                setErrorMsgEmail('El campo email es obligatorio');
            } else if( helperValidations.maxLength(email.length, 200) ) {
                setErrorMsgEmail('La longitud máxima del campo email es 200');
            } else {
                setErrorMsgEmail('Debes ingresar un e-mail válido');
            }
        }
        setEmail(email);
    };

    const onChangeEmailConfirmation = (event: any) => {
        const { value } = event.target;
        const emailConfirmation = value;
        if( helperValidations.lengthBetween(emailConfirmation.length, 1, 200) &&
            helperValidations.emailFormat(String(emailConfirmation)) && emailConfirmation === email ) {
            setEmailIsValid(true);
            setErrorMsgEmailConfirmation('');
            setErrorMsgEmail('');
        } else {
            setEmailIsValid(false);
            if( emailConfirmation !== email ) {
                setErrorMsgEmailConfirmation('Los e-mails ingresados no coinciden');
            } else {
                setErrorMsgEmailConfirmation('El e-mail ingresado no es válido');
            }
        }
        setEmailConfirmation(emailConfirmation);
    };

    const onChangePassword = (event: any) => {
        const { value } = event.target;
        const password = value;
        if( helperValidations.lengthBetween(password.length, 8, 50) && helperValidations.lowerCaseChar(password)
            && helperValidations.upperCaseChar(password) && helperValidations.numberChar(password)
            && helperValidations.specialChar(password) && helperValidations.noConsecutiveDigits(password) ) {
            setPasswordIsValid(true);
            setErrorMsgPassword('');
        } else {
            setPasswordIsValid(false);
            setErrorMsgPassword('La contraseña debe tener: al menos 1 mayúscula, al menos 1 minúscula, al menos 1 caracter especial, al menos 1 número y si hay dos dígitos seguidos no pueden ser iguales. Longitud mínima 8 y máxima 50 caracteres.');
        }
        setPassword(password);
    };

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        setChecked(event.target.checked);
    };

    const showPasswordClick = () => {
        setShowPassword(!showPassword)
    };
    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const classes = useStyles();

    return (
        <React.Fragment>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={4}>
                    <Grid item xs={12}>
                        <Box component="span" m={1}>
                            <h2>Genial! 🙌</h2>
                            <p>Ahora, algunos datos para identificarte:</p>
                            <p>CUIT: {cuit}</p>
                            <p>Razón Social: {establishments[0].business_name}</p>
                        </Box>
                        <TextField
                            id={'name'}
                            name="name"
                            variant="filled"
                            label="Nombre *"
                            value={name}
                            onChange={ onChangeName }
                            error={ !!errorMsgName }
                            helperText={ errorMsgName ?? "Ingrese nombre" }
                            fullWidth
                            autoFocus
                        />
                        <p>Completo, como figura en tu DNI</p>
                        <TextField
                            id={'surname'}
                            name="surname"
                            variant="filled"
                            label="Apellido *"
                            value={surname}
                            onChange={onChangeSurname}
                            error={!!errorMsgSurname}
                            helperText={errorMsgSurname ?? "Ingrese apellido"}
                            fullWidth
                        />
                        <p>Completo, como figura en tu DNI</p>
                        <TextField
                            id={'email'}
                            name="email"
                            variant="filled"
                            label="Email *"
                            value={email}
                            onChange={onChangeEmail}
                            error={!!errorMsgEmail}
                            helperText={errorMsgEmail ?? "Ingrese email"}
                            fullWidth
                        />
                        <p></p>
                        <TextField
                            id={'email-confirmation'}
                            name="emailConfirmation"
                            variant="filled"
                            label="Confirmar email *"
                            value={emailConfirmation}
                            onChange={onChangeEmailConfirmation}
                            error={!!errorMsgEmailConfirmation}
                            helperText={errorMsgEmailConfirmation ?? "Confirme su mail "}
                            fullWidth
                        />
                        <p></p>
                         <FormControl variant="filled" fullWidth>
                             <InputLabel htmlFor="filled-adornment-password">Contraseña *</InputLabel>
                             <FilledInput
                                 id={'password'}
                                 name="password"
                                 type={showPassword ? 'text' : 'password'}
                                 value={password}
                                 onChange={onChangePassword}
                                 error={!!errorMsgPassword}
                                 fullWidth
                                 endAdornment={
                                     <InputAdornment position="end">
                                         <IconButton
                                             aria-label="toggle password visibility"
                                             onClick={showPasswordClick}
                                             onMouseDown={handleMouseDownPassword}
                                             edge="end"
                                         >
                                             {showPassword ? <Visibility /> : <VisibilityOff />}
                                         </IconButton>
                                     </InputAdornment>
                                 }
                             />
                         </FormControl>

                        <p>Tu contraseña debe tener por lo menos: 8 caracteres, una mayúscula, una minúscula, un caracter especial (#?!@$%^&*-), un número, y si hay dos dígitos seguidos, no pueden ser iguales.
                        </p>
                        <FormControlLabel
                            control={
                            <Checkbox
                                id={'terms-and-conditions'}
                                checked={checked}
                                onChange={handleChange}
                                name="checkedA"
                            />}
                            label={ <TerminosCondiciones/>}
                        />

                        <FormHelperText>Para continuar acepte los términos y condiciones</FormHelperText>
                    </Grid>
                    <Grid item xs={12}>
                        <div className={classes.buttonAction}>
                            <Button
                                className={classes.buttonContinuarStyle}
                                id={'account-submit-button'}
                                variant="contained"
                                color="primary"
                                type="submit"
                                disabled={ (!nameIsValid || !surnameIsValid || !emailIsValid || !passwordIsValid || !checked ) || loading }
                            >
                                Crear Cuenta
                            </Button>
                            {loading && <CircularProgress size={24} />}
                        </div>
                    </Grid>
                    <Grid item xs={12}>
                        <div>
                            <FormHelperText error={errorEmailExists}>{ errorMsg }</FormHelperText>
                        </div>
                    </Grid>
                </Grid>
            </form>
            <OtpModal
                showModal={showModal}
                setShowModal={setShowModal}
                email={email}
                setModalWasConfirmed={setModalWasConfirmed}
                setLoadingAccountForm={setLoading}
            />
        </React.Fragment>
    );
}

export default AccountForm;
